import {Group, Modal, Button} from '@mantine/core';
import React from 'react';

import {isNodeTesting} from 'lib/Browser';
import ModalFooter from 'ui-library/modals/ModalFooter';

import {FeedbackContextItem} from './LLMFeedbackConstants';
import {LLMFeedbackFormContents, useFeedbackForm} from './LLMFeedbackFormContents';

export interface LLMFeedbackModalProps {
  /**
   * The title of the feedback modal
   */
  title: string;
  /**
   * Optional context items that the user can opt-out of including with their feedback
   */
  optionalContextItems: Array<FeedbackContextItem>;
  /**
   * Required context items that are not displayed to the user and always included with their feedback
   */
  requiredContextItems: Array<FeedbackContextItem>;
  opened: boolean;
  onRequestClose: () => void;
  /**
   * If the modal needs to be rendered over a tooltip that opened it, set zIndex to 1060. However, this has
   * potential negative consequences if overused; for example, tooltips opened from within the modal (like
   * from a chip rendered in the modal) may be rendered behind the modal. So a good rule of thumb is only
   * to override this prop if you are confident your modal will never trigger any tooltips and it is needed
   * for the modal to display properly.
   *
   * @default 1040, set from `modals.less`
   */
  zIndex?: 1040 | 1060;
}

export default function LLMFeedbackModal(props: LLMFeedbackModalProps): JSX.Element {
  const {disabled, submitForm, formProps} = useFeedbackForm(props);

  const handleSubmit = async (): Promise<void> => {
    await submitForm();
    props.onRequestClose();
  };

  return (
    <Modal
      opened={props.opened}
      onClose={props.onRequestClose}
      title={props.title}
      closeOnClickOutside={true}
      closeOnEscape={true}
      // Legacy Enzyme test workaround. New components with RTL should omit withinPortal.
      withinPortal={!isNodeTesting}
      zIndex={props.zIndex ?? 1040} // Default ui-library modals zIndex is 1040
    >
      <>
        <LLMFeedbackFormContents isPositiveFeedback={false} {...formProps} />
        <ModalFooter>
          <Group position="right">
            <Button disabled={disabled} onClick={handleSubmit}>
              Submit feedback
            </Button>
          </Group>
        </ModalFooter>
      </>
    </Modal>
  );
}
